import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Card, CardMedia, CardContent,
  CardActions, IconButton, Tooltip, Chip, Grid, Alert, Divider,
} from '@mui/material';
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';

const COLLECTION_CONFIG = {
  games:        { label: 'Video Games',   endpoint: '/games',        nameField: 'title' },
  consoles:     { label: 'Consoles',      endpoint: '/consoles',     nameField: 'name' },
  lego:         { label: 'LEGO',          endpoint: '/lego',         nameField: 'name' },
  movies:       { label: 'Movies',        endpoint: '/movies',       nameField: 'title' },
  music:        { label: 'Music',         endpoint: '/music',        nameField: 'title' },
  books:        { label: 'Books',         endpoint: '/books',        nameField: 'title' },
  boardGames:   { label: 'Board Games',   endpoint: '/board-games',  nameField: 'name' },
  tradingCards: { label: 'Trading Cards', endpoint: '/trading-cards', nameField: 'name' },
  manga:        { label: 'Manga',         endpoint: '/manga',        nameField: 'title' },
  comics:       { label: 'Comics',        endpoint: '/comics',       nameField: 'series' },
};

const DETAIL_URLS = {
  games:        (id) => `/games/${id}`,
  consoles:     (id) => `/consoles/${id}`,
  lego:         (id) => `/lego/${id}`,
  movies:       (id) => `/movies/${id}`,
  music:        (id) => `/music/${id}`,
  books:        (id) => `/books/${id}`,
  boardGames:   (id) => `/board-games/${id}`,
  tradingCards: (id) => `/trading-cards/${id}`,
  manga:        (id) => `/manga/${id}`,
  comics:       (id) => `/comics/${id}`,
};

const getItemName = (item, key) => {
  const { nameField } = COLLECTION_CONFIG[key];
  if (key === 'comics') {
    return item.series && item.issueNumber ? `${item.series} #${item.issueNumber}` : (item.series || 'Unknown');
  }
  if (key === 'music') {
    return item.artist && item.title ? `${item.artist} — ${item.title}` : (item.title || item.artist || 'Unknown');
  }
  return item[nameField] || 'Unknown';
};

const getItemImage = (item) => item.processedImageUrl || item.imageUrl || item.platformLogo || null;

const BacklogItem = ({ item, typeKey, onRemove }) => {
  const name = getItemName(item, typeKey);
  const image = getItemImage(item);
  const detailUrl = DETAIL_URLS[typeKey] ? DETAIL_URLS[typeKey](item._id) : null;

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardMedia
        component="img"
        image={image || '/placeholder.png'}
        alt={name}
        sx={{ height: 160, objectFit: 'contain', bgcolor: 'background.default', p: 0.5 }}
        onError={(e) => { e.target.src = '/placeholder.png'; }}
      />
      <CardContent sx={{ flex: 1, pb: 0 }}>
        <Typography variant="body2" fontWeight="medium" sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {name}
        </Typography>
        {item.rating && (
          <Typography variant="caption" color="text.secondary">
            {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        {detailUrl && (
          <Tooltip title="View item">
            <IconButton size="small" href={detailUrl}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Remove from backlog">
          <IconButton size="small" color="warning" onClick={() => onRemove(item._id)}>
            <BookmarkRemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const BacklogPage = () => {
  const [backlog, setBacklog] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBacklog = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/backlog');
      setBacklog(data.backlog);
      setTotal(data.total);
    } catch (err) {
      setError('Failed to load backlog.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBacklog(); }, [fetchBacklog]);

  const handleRemove = useCallback(async (typeKey, itemId) => {
    const { endpoint } = COLLECTION_CONFIG[typeKey];
    try {
      await axiosInstance.put(`${endpoint}/${itemId}`, { inBacklog: false });
      setBacklog(prev => ({
        ...prev,
        [typeKey]: prev[typeKey].filter(i => i._id !== itemId),
      }));
      setTotal(prev => prev - 1);
    } catch (err) {
      // silently ignore — item stays in list, user can retry
    }
  }, []);

  const sections = Object.entries(COLLECTION_CONFIG).filter(
    ([key]) => backlog[key] && backlog[key].length > 0
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBarHeader />
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">My Backlog</Typography>
          {!loading && (
            <Chip label={`${total} item${total !== 1 ? 's' : ''}`} size="small" color="primary" />
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && total === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your backlog is empty
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add items to your backlog from any collection using the bookmark icon.
            </Typography>
          </Box>
        )}

        {!loading && sections.map(([key, { label }], idx) => (
          <Box key={key} sx={{ mb: 4 }}>
            {idx > 0 && <Divider sx={{ mb: 3 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6">{label}</Typography>
              <Chip
                label={backlog[key].length}
                size="small"
                variant="outlined"
              />
            </Box>
            <Grid container spacing={2}>
              {backlog[key].map(item => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={item._id}>
                  <BacklogItem
                    item={item}
                    typeKey={key}
                    onRemove={(id) => handleRemove(key, id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BacklogPage;
